"use client";

import { useState } from "react";

import TrackedCtaButton from "@/components/analytics/TrackedCtaButton";
import ServiceReviewSummaryStrip from "@/app/services/[...iden]/components/ServiceReviewSummaryStrip";
import BookCallDialog from "@/app/services/[...iden]/components/BookCallDialog";
import { useServiceCountdownOfferOverlay } from "@/hooks/useServiceCountdownOfferOverlay";
import type { Service } from "@/types";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";

function scrollToElement(id: string, offsetPx = 100) {
	if (typeof window === "undefined") return;
	const target = document.getElementById(id);
	if (!target) return;
	const y = target.getBoundingClientRect().top + window.scrollY - offsetPx;
	window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
}

export default function ServicePostGuaranteeCta({ service }: { service: Service }) {
	const [bookCallOpen, setBookCallOpen] = useState(false);
	// Match countdown banner: Book a Call only while active countdown or 2h ended grace.
	const offerOverlay = useServiceCountdownOfferOverlay(service);
	const showBookACall = offerOverlay !== null;
	const trialEnabled =
		offerOverlay?.variant === "active" && service.countdownOffer?.showFreeTrial === true;
	const launchOfferPercent =
		offerOverlay?.variant === "active" &&
		typeof service.countdownOffer?.discountPercent === "number" &&
		Number.isFinite(service.countdownOffer.discountPercent) &&
		service.countdownOffer.discountPercent > 0
			? Math.min(100, Math.max(0, Math.round(service.countdownOffer.discountPercent)))
			: undefined;

	const primaryCta = trialEnabled
		? {
				id: "start_14_day_free_trial_post_guarantee" as const,
				name: "Start your 14-day free trial",
			}
		: launchOfferPercent !== undefined
			? {
					id: "get_launch_offer_post_guarantee" as const,
					name: `Get your launch offer (${launchOfferPercent}% off)`,
				}
			: {
					id: "buy_now_post_guarantee" as const,
					name: "Buy now",
				};

	const openSupportChat = () => {
		window.dispatchEvent(
			new CustomEvent("techxeed-open-support-chat", { detail: { source: "get_in_touch" } }),
		);
	};

	const triggerCheckout = () => {
		const checkoutButton = document
			.getElementById("service-package-card")
			?.querySelector<HTMLButtonElement>("button#checkout");
		if (checkoutButton) {
			checkoutButton.click();
			return;
		}
		scrollToElement("service-package-card", 120);
	};

	return (
		<section className="bg-background pb-10 pt-2">
			<div className="container px-[15px] sm:px-8">
				<div className="mx-auto flex w-full max-w-[560px] flex-row items-center justify-center gap-2 sm:gap-4">
					<TrackedCtaButton
						type="button"
						ctaId={showBookACall ? "book_a_call_post_guarantee" : "get_in_touch_post_guarantee"}
						ctaName={showBookACall ? "Book a call" : "Get in Touch"}
						ctaLocation="service_post_guarantee"
						onClick={() => {
							if (showBookACall) {
								setBookCallOpen(true);
								return;
							}
							openSupportChat();
						}}
						className="h-11 min-w-0 flex-1 basis-0 whitespace-nowrap rounded-full bg-secondary px-2 text-[clamp(0.625rem,2.5vw,0.75rem)] font-semibold leading-none text-secondary-foreground shadow-md hover:bg-secondary/90 sm:h-12 sm:min-w-[265px] sm:flex-none sm:px-8 sm:text-sm"
					>
						{showBookACall ? "Book a call" : "Get in Touch"}
					</TrackedCtaButton>
					<TrackedCtaButton
						type="button"
						ctaId={primaryCta.id}
						ctaName={primaryCta.name}
						ctaLocation="service_post_guarantee"
						onClick={triggerCheckout}
						className="h-11 min-w-0 flex-[1.35] basis-0 whitespace-nowrap rounded-full bg-tertiary px-1.5 text-[clamp(0.5625rem,2.3vw,0.75rem)] font-semibold leading-none text-white shadow-md hover:bg-tertiary/90 sm:h-12 sm:min-w-[265px] sm:flex-none sm:px-8 sm:text-sm"
					>
						{primaryCta.name}
					</TrackedCtaButton>
				</div>
				<ServiceReviewSummaryStrip ctaLocation="service_post_guarantee_review_summary" />
			</div>
			<BookCallDialog
				open={bookCallOpen}
				onOpenChange={setBookCallOpen}
				serviceTitle={formatServiceDisplayTitle(service.title)}
			/>
		</section>
	);
}
